import { Pipe, PipeTransform } from '@angular/core';

/**
 * One place for the "<Label>: <Title> <Name> <Surname>" toolbar heading used across the
 * patient file and every detail page reached from it.
 *
 *   {{ patient | patientHeading:'Clinical Note' }}           -> "Clinical Note: Dr Ada Lovelace"
 *   {{ patient | patientHeading:'Edit':'Add Patient' }}      -> "Edit: Dr Ada Lovelace", or
 *                                                               "Add Patient" while there is no patient
 *
 * Before this pipe, five pages each rebuilt the expression by hand and the rest showed a bare
 * label. Name parts that are empty are skipped, so a patient without a title does not get the
 * double space the hand-written form produced.
 */
/** The name half of the heading, for callers that need it without a label. */
export interface PatientNameParts {
  title?: string | null;
  name?: string | null;
  surname?: string | null;
}

/**
 * "Dr Ada Lovelace" from a patient, or '' when there is nothing to show.
 *
 * Exported because a name is also stored against a recording in IndexedDB, where the
 * "<Label>: " prefix makes no sense. The hand-written `${patient.name} ${patient.surname}`
 * that used to do that job rendered a patient with no surname as "Ada undefined".
 */
export function patientFullName(patient: PatientNameParts | null | undefined): string {
  if (!patient) return '';
  return [patient.title, patient.name, patient.surname]
    .map((p) => (p ?? '').toString().trim())
    .filter(Boolean)
    .join(' ');
}

@Pipe({ name: 'patientHeading' })
export class PatientHeadingPipe implements PipeTransform {
  transform(
    patient: PatientNameParts | null | undefined,
    label: string,
    fallback?: string,
  ): string {
    if (!patient) return fallback ?? label;
    const fullName = patientFullName(patient);
    return fullName ? `${label}: ${fullName}` : label;
  }
}
